/**
 * Token-bound monograph controls shared across companion demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { cva } from "class-variance-authority";
import { Check } from "lucide-react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type {
  MonographChipMultiSelectProps,
  MonographPanelTokens,
} from "./MonographPanelPrimitiveTypes.js";

export type MonographControlTone = "filled" | "ghost";
export type MonographControlSize = "sm" | "md";

export interface MonographControlButtonProps {
  readonly tokens: MonographPanelTokens;
  readonly children?: React.ReactNode;
  readonly label?: React.ReactNode;
  readonly ariaLabel?: string;
  readonly onClick?: () => void;
  readonly color?: string;
  readonly disabled?: boolean;
  readonly tone?: MonographControlTone;
  readonly size?: MonographControlSize;
  readonly title?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export interface MonographControlBarProps {
  readonly children: React.ReactNode;
  readonly label?: React.ReactNode;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export interface MonographChoiceChipProps<Id extends string = string> {
  readonly tokens: MonographPanelTokens;
  readonly id: Id;
  readonly label: React.ReactNode;
  readonly active: boolean;
  readonly onSelect: (id: Id) => void;
  readonly color?: string;
  readonly role?: "button" | "radio" | "tab";
  readonly title?: string;
  readonly className?: string;
}

export interface MonographChoiceGroupItem<Id extends string = string> {
  readonly id: Id;
  readonly label: React.ReactNode;
  readonly color?: string;
  readonly title?: string;
}

export interface MonographChoiceGroupProps<Id extends string = string> {
  readonly tokens: MonographPanelTokens;
  readonly items: readonly MonographChoiceGroupItem<Id>[];
  readonly value: Id;
  readonly onChange: (id: Id) => void;
  readonly ariaLabel: string;
  readonly color?: string;
  readonly className?: string;
}

export interface MonographNumberStepperProps {
  readonly tokens: MonographPanelTokens;
  readonly label: React.ReactNode;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly onChange: (next: number) => void;
  readonly color?: string;
  readonly step?: number;
  readonly className?: string;
}

const chipClass = cva("csk-monograph-chip-multi-select__chip", {
  variants: {
    active: {
      false: "csk-monograph-chip-multi-select__chip--inactive",
      true: "csk-monograph-chip-multi-select__chip--active",
    },
  },
  defaultVariants: { active: false },
});

interface ControlFactoryInput {
  readonly tokens: MonographPanelTokens;
}

export function createMonographPanelControls({ tokens }: ControlFactoryInput): {
  readonly ChipMultiSelect: <Id extends string = string>(
    props: MonographChipMultiSelectProps<Id>,
  ) => React.JSX.Element;
} {
  function ChipMultiSelect<Id extends string = string>({
    items,
    values,
    onChange,
    ariaLabel,
    color = tokens.accent,
    className,
  }: MonographChipMultiSelectProps<Id>): React.JSX.Element {
    const [parent] = useAutoAnimate<HTMLDivElement>();
    const selected = React.useMemo(() => new Set(values), [values]);

    return (
      <div
        ref={parent}
        role="group"
        aria-label={ariaLabel}
        className={cn("csk-monograph-chip-multi-select", className)}
        style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
      >
        {items.map((item) => {
          const active = selected.has(item.id);
          const tone = item.color ?? color;
          return (
            <button
              key={item.id}
              type="button"
              aria-pressed={active}
              title={item.title}
              className={chipClass({ active })}
              onClick={() => {
                onChange(
                  active ? values.filter((value) => value !== item.id) : [...values, item.id],
                );
              }}
              style={{
                alignItems: "center",
                background: active ? colorWithAlpha(tone, "22") : "rgba(7,6,13,0.42)",
                border: active ? `1px solid ${tone}` : `1px solid ${tokens.border}`,
                borderRadius: 999,
                boxShadow: active ? `0 0 14px ${colorWithAlpha(tone, "2f")}` : "none",
                color: active ? tone : tokens.text,
                cursor: "pointer",
                display: "inline-flex",
                fontFamily: tokens.mono,
                fontSize: 12,
                fontWeight: 700,
                gap: 6,
                letterSpacing: 0,
                minHeight: 32,
                padding: "7px 12px",
                transition: "background 140ms ease, border-color 140ms ease, color 140ms ease",
              }}
            >
              {active ? <Check aria-hidden="true" size={13} /> : null}
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return { ChipMultiSelect };
}

export function MonographControlBar({
  children,
  label,
  ariaLabel,
  className,
  style,
}: MonographControlBarProps): React.JSX.Element {
  return (
    <div
      aria-label={ariaLabel ?? (typeof label === "string" ? label : undefined)}
      className={cn("csk-monograph-control-bar", className)}
      style={{
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        ...style,
      }}
    >
      {label ? (
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0,
            opacity: 0.72,
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
      ) : null}
      {children}
    </div>
  );
}

export function MonographControlButton({
  tokens,
  children,
  label,
  ariaLabel,
  onClick,
  color = tokens.accent,
  disabled = false,
  tone = "filled",
  size = "md",
  title,
  className,
  style,
}: MonographControlButtonProps): React.JSX.Element {
  const filled = tone === "filled";
  const compact = size === "sm";
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={cn("csk-monograph-control-button", className)}
      style={{
        background: disabled
          ? colorWithAlpha(tokens.border, "40")
          : filled
            ? colorWithAlpha(color, "22")
            : colorWithAlpha(tokens.void, "66"),
        border: `1px solid ${disabled ? tokens.border : colorWithAlpha(color, "66")}`,
        borderRadius: 8,
        color: disabled ? tokens.muted : color,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: tokens.mono,
        fontSize: compact ? 10.5 : 12,
        fontWeight: 700,
        minHeight: compact ? 24 : 32,
        opacity: disabled ? 0.5 : 1,
        padding: compact ? "3px 8px" : "7px 12px",
        transition: "background 140ms ease, border-color 140ms ease, color 140ms ease",
        ...style,
      }}
    >
      {children ?? label}
    </button>
  );
}

export function MonographChoiceChip<Id extends string = string>({
  tokens,
  id,
  label,
  active,
  onSelect,
  color = tokens.accent,
  role = "tab",
  title,
  className,
}: MonographChoiceChipProps<Id>): React.JSX.Element {
  const activeProps =
    role === "radio"
      ? { "aria-checked": active }
      : role === "button"
        ? { "aria-pressed": active }
        : { "aria-selected": active };
  return (
    <button
      type="button"
      role={role === "button" ? undefined : role}
      {...activeProps}
      title={title}
      onClick={() => {
        onSelect(id);
      }}
      className={cn("csk-monograph-choice-chip", className)}
      style={{
        background: active ? colorWithAlpha(color, "22") : colorWithAlpha(tokens.void, "66"),
        border: `1px solid ${active ? color : tokens.border}`,
        borderRadius: 999,
        boxShadow: active ? `0 0 14px ${colorWithAlpha(color, "33")}` : "none",
        color: active ? color : tokens.text,
        cursor: "pointer",
        fontFamily: tokens.mono,
        fontSize: 12,
        fontWeight: active ? 800 : 600,
        letterSpacing: 0,
        minHeight: 30,
        padding: "6px 11px",
        transition: "background 140ms ease, border-color 140ms ease, color 140ms ease",
      }}
    >
      {label}
    </button>
  );
}

export function MonographChoiceGroup<Id extends string = string>({
  tokens,
  items,
  value,
  onChange,
  ariaLabel,
  color = tokens.accent,
  className,
}: MonographChoiceGroupProps<Id>): React.JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn("csk-monograph-choice-group", className)}
      style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
    >
      {items.map((item) => (
        <MonographChoiceChip
          key={item.id}
          tokens={tokens}
          id={item.id}
          label={item.label}
          active={item.id === value}
          onSelect={onChange}
          color={item.color ?? color}
          {...(item.title ? { title: item.title } : {})}
        />
      ))}
    </div>
  );
}

export function MonographNumberStepper({
  tokens,
  label,
  value,
  min,
  max,
  onChange,
  color = tokens.accent,
  step = 1,
  className,
}: MonographNumberStepperProps): React.JSX.Element {
  const labelText = typeof label === "string" ? label : "value";
  const clamp = (next: number): number => Math.max(min, Math.min(max, next));
  const decrement = clamp(value - step);
  const increment = clamp(value + step);
  return (
    <div
      className={cn("csk-monograph-number-stepper", className)}
      style={{ alignItems: "center", display: "inline-flex", gap: 8 }}
    >
      <span
        style={{
          color: tokens.muted,
          fontFamily: tokens.mono,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0,
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <MonographControlButton
        tokens={tokens}
        color={tokens.text}
        disabled={decrement === value}
        onClick={() => {
          onChange(decrement);
        }}
        style={{ minHeight: 28, padding: "4px 9px" }}
        ariaLabel={`Decrease ${labelText}`}
        title={`Decrease ${labelText}`}
        tone="ghost"
      >
        -
      </MonographControlButton>
      <span
        style={{
          color,
          fontFamily: tokens.mono,
          fontSize: 16,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 800,
          minWidth: 32,
          textAlign: "center",
          textShadow: `0 0 10px ${colorWithAlpha(color, "55")}`,
        }}
      >
        {value}
      </span>
      <MonographControlButton
        tokens={tokens}
        color={tokens.text}
        disabled={increment === value}
        onClick={() => {
          onChange(increment);
        }}
        style={{ minHeight: 28, padding: "4px 9px" }}
        ariaLabel={`Increase ${labelText}`}
        title={`Increase ${labelText}`}
        tone="ghost"
      >
        +
      </MonographControlButton>
    </div>
  );
}
